import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Clock, Gauge, Check, ShieldAlert, Lightbulb, HeartPulse, ListOrdered, Loader2, MessageCircle } from "lucide-react";
import { api, openWhatsApp } from "@/lib/api";
import { DIFFICULTY_STYLES } from "@/lib/exercises";

export default function ExerciseDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ex, setEx] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setEx(null);
    api.get(`/exercises/${id}`).then((r) => setEx(r.data)).catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center">
        <p className="text-sm text-slate-500">Exercise not found.</p>
        <button data-testid="exercise-details-back-list" onClick={() => navigate("/exercise")} className="mt-3 text-sm font-bold text-emerald-700">Back to list</button>
      </div>
    );
  }
  if (!ex) {
    return <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  }

  return (
    <div className="animate-fade-in pb-6" data-testid="exercise-details-page">
      <div className="relative h-64 bg-slate-100 dark:bg-emerald-800">
        <img src={ex.image} alt={ex.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <button
          data-testid="exercise-details-back-btn"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 dark:bg-emerald-950/90 backdrop-blur flex items-center justify-center text-slate-700 dark:text-slate-100 shadow"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="absolute bottom-4 left-5 right-5">
          <span className="text-[11px] font-semibold tracking-wider uppercase text-emerald-200">{ex.category}</span>
          <h1 data-testid="exercise-details-name" className="text-2xl font-extrabold text-white font-head leading-tight">{ex.name}</h1>
        </div>
      </div>

      <div className="px-5 -mt-4 relative">
        <div className="grid grid-cols-2 gap-3">
          <Stat icon={Gauge} label="Difficulty">
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${DIFFICULTY_STYLES[ex.difficulty] || DIFFICULTY_STYLES.Beginner}`}>{ex.difficulty}</span>
          </Stat>
          <Stat icon={Clock} label="Suggested">
            <span className="text-xs font-semibold text-emerald-950 dark:text-emerald-50 leading-snug">{ex.duration}</span>
          </Stat>
        </div>
      </div>

      <div className="px-5 mt-5 space-y-5">
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed" data-testid="exercise-details-intro">{ex.intro}</p>

        <Section icon={ListOrdered} title="How to Perform">
          <ol className="space-y-2.5">
            {ex.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                <span className="w-6 h-6 rounded-full bg-emerald-700 dark:bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="leading-relaxed pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
        </Section>

        <Section icon={HeartPulse} title="May Support / Suitable For">
          <div className="flex flex-wrap gap-1.5">
            {ex.suitable_for.map((s) => (
              <span key={s} className="text-xs font-semibold text-emerald-800 dark:text-emerald-200 bg-emerald-100 dark:bg-emerald-800 px-2.5 py-1 rounded-full">{s}</span>
            ))}
          </div>
          {ex.tags?.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Health tags</p>
              <div className="flex flex-wrap gap-1.5">
                {ex.tags.map((t) => (
                  <span key={t} className="text-[11px] text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-emerald-900 px-2 py-0.5 rounded-full">{t}</span>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section icon={ShieldAlert} title="Safety Precautions" tone="amber">
          <ul className="space-y-2">
            {ex.precautions.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-amber-900 dark:text-amber-100">
                <Check className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <span className="leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Lightbulb} title="Beginner Tip" tone="gold">
          <p className="text-sm text-emerald-950 dark:text-emerald-50 leading-relaxed italic">“{ex.tip}”</p>
        </Section>

        <button
          data-testid="exercise-consult-btn"
          onClick={() => openWhatsApp(`Hello U&URS NUTRITIONIST 👋\n\nI'd like personalised guidance on "${ex.name}" as part of my fitness & nutrition plan.`)}
          className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-2xl transition-colors shadow-lg shadow-emerald-600/20"
        >
          <MessageCircle className="w-5 h-5" /> Ask the Nutritionist
        </button>
        <p className="text-[11px] text-slate-400 text-center leading-relaxed">General educational guidance only. Not a substitute for medical advice.</p>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, children }) => (
  <div className="p-3.5 rounded-2xl bg-white dark:bg-emerald-900 border border-slate-100 dark:border-emerald-800 shadow-sm">
    <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mb-1.5"><Icon className="w-3.5 h-3.5 text-emerald-600" />{label}</div>
    {children}
  </div>
);

const TONES = {
  default: "bg-white dark:bg-emerald-900/60 border-slate-100 dark:border-emerald-800",
  amber: "bg-amber-50 dark:bg-amber-500/10 border-amber-100 dark:border-amber-500/20",
  gold: "bg-[#faf6ea] dark:bg-emerald-900/60 border-[#eadfb9] dark:border-emerald-800",
};

const Section = ({ icon: Icon, title, tone = "default", children }) => (
  <div className={`rounded-2xl border p-4 ${TONES[tone]}`}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4.5 h-4.5 text-emerald-700 dark:text-emerald-300" />
      <h3 className="text-sm font-bold text-emerald-950 dark:text-emerald-50 font-head">{title}</h3>
    </div>
    {children}
  </div>
);
